<script lang="ts" setup>
import { SplitPanel } from '@directus/vue-split-panel';
import { computed, useTemplateRef } from 'vue';
import { PANEL_DEFAULT_SIZE, PANEL_MAX_SIZE, PANEL_MIN_SIZE, SplitPanelPane, usePaneStore } from '#layers/ui/public';
import WorkspaceChromeDivider from './WorkspaceChromeDivider.vue';

const paneStore = usePaneStore();

const splitContainer = useTemplateRef<{ $el: HTMLElement }>('splitContainer');
const drawerContainer = useTemplateRef<HTMLElement>('drawerContainer');

const teleportTarget = computed(() => (paneStore.panelUseSplitPanel ? splitContainer.value?.$el : drawerContainer.value));
</script>

<template>
	<SplitPanel
		:key="!paneStore.currentPanel?.component ? 'no-panel' : 'with-panel'"
		v-model:size="paneStore.panelSize"
		v-model:collapsed="paneStore.panelSplitPanelCollapsed"
		class="min-h-0 grow"
		size-unit="px"
		collapsible
		:collapse-threshold="70"
		:min-size="PANEL_MIN_SIZE"
		:max-size="PANEL_MAX_SIZE"
		:snap-points="[PANEL_DEFAULT_SIZE]"
		:snap-threshold="6"
		:ui="{
			start: 'flex flex-col',
			divider: 'focus-visible:ring-1',
		}"
		divider-hit-area="24px"
		:transition-duration="150"
		primary="end"
		:disabled="!paneStore.panelUseSplitPanel"
	>
		<template #start>
			<slot />
		</template>

		<template #divider>
			<WorkspaceChromeDivider v-show="paneStore.panelUseSplitPanel" :collapsed="paneStore.panelSplitPanelCollapsed" placement="end" />
		</template>

		<template v-if="paneStore.panelUseSplitPanel" #end>
			<SplitPanelPane ref="splitContainer" :size="paneStore.panelSize" side="end" />
		</template>
	</SplitPanel>

	<DDrawer
		:open="paneStore.panelDrawerVisible"
		direction="right"
		:handle="false"
		:ui="{ content: `w-[calc(100vw-var(--spacebar-width))] sm:w-[360px]` }"
		@update:open="paneStore.panelDrawerVisible = $event"
	>
		<template #content>
			<div class="flex size-full flex-col overflow-hidden rounded-lg bg-default ring ring-default">
				<div ref="drawerContainer" class="flex h-full flex-col" />
			</div>
		</template>
	</DDrawer>

	<div v-if="paneStore.currentPanel?.component" class="hidden">
		<Teleport :to="teleportTarget" :disabled="!teleportTarget" defer>
			<component :is="paneStore.currentPanel.component" />
		</Teleport>
	</div>
</template>
